/**
 * Centralized user-facing error messages
 * These messages are safe to display to users in production
 * Firebase error codes ref: https://firebase.google.com/docs/auth/admin/errors
 */

// Authentication errors
export const AUTH_ERROR = {
  INVALID_CREDENTIALS: 'Invalid credentials',
  INVALID_EMAIL_OR_PASSWORD: 'Invalid email or password',
  EMAIL_NOT_VERIFIED:
    'Email not verified. Please verify your email before logging in.',
  CURRENT_PASSWORD_INCORRECT: 'Current password is incorrect',
  INVALID_EMAIL_ADDRESS: 'Invalid email address',
  INVALID_EMAIL_FORMAT: 'Invalid email address format',
  INVALID_PASSWORD: 'Invalid password. Please use at least 8 characters.',
  ACCOUNT_DISABLED: 'This account has been disabled. Please contact support.',
  TOO_MANY_LOGIN_ATTEMPTS:
    'Too many failed login attempts. Please try again later.',
  SIGN_IN_METHOD_DISABLED:
    'This sign-in method is not enabled. Please contact support.',
  LOGIN_FAILED: 'Login failed. Please check your credentials and try again.',
  EMAIL_ALREADY_EXISTS: 'An account with this email already exists',
  REGISTRATION_DISABLED:
    'Registration is currently disabled. Please contact support.',
  REGISTRATION_FAILED:
    'Registration failed. Please check your information and try again.',
  PASSWORD_RESET_FAILED: 'Password reset failed. Please try again later.',
  NO_ACCOUNT_FOUND: 'No account found with this email address',
  REFRESH_TOKEN_EXPIRED: 'Refresh token expired. Please log in again.',
  INVALID_REFRESH_TOKEN: 'Invalid refresh token',
  TURNSTILE_FAILED: 'Turnstile verification failed. Please try again.',
  INVALID_EMAIL_DOMAIN:
    'Registration is restricted to CityU email addresses (my.cityu.edu.hk or cityu.edu.hk).',
  EMAIL_ROLE_MISMATCH:
    'This email domain does not match the selected role. Students use a my.cityu.edu.hk address; Faculty use a cityu.edu.hk address.',
  COHORT_GROUP_REQUIRED: 'Please select a cohort group.',
  COHORT_GROUP_NOT_FOUND: 'Selected cohort group not found.',
} as const;

// Resource errors
export const RESOURCE_ERROR = {
  USER_NOT_FOUND: 'User not found',
  ITEM_NOT_FOUND: 'Item not found',
  FILE_NOT_FOUND: 'File not found',
  MODEL_NOT_FOUND: 'Model file not found',
  ALREADY_EXISTS: 'already exists',
  EXPIRED: 'expired',
} as const;

// Quota & credits errors
export const QUOTA_ERROR = {
  INSUFFICIENT_SCAN_CREDITS: 'Insufficient scan credits',
  INSUFFICIENT_EMBED_QUOTA: 'Insufficient embed quota',
  SCENE_QUOTA_EXCEEDED: 'Scene quota exceeded',
} as const;

// Access control errors
export const ACCESS_ERROR = {
  NOT_ITEM_OWNER: 'User is not item owner.',
  ACCESS_DENIED: 'Access denied.',
} as const;

// Status & feature restriction errors
export const FEATURE_ERROR = {
  ONLY_PUBLISHED_CAN_EMBED: 'Only published items can be embedded',
  ITEM_NOT_PUBLISHED: 'Item is not published.',
  SCENE_ADMIN_ONLY:
    "Building scenes isn't included in your plan. Please contact the admin if you'd like to use this feature.",
} as const;

// Operation errors
export const OPERATION_ERROR = {
  TOO_MANY_REQUESTS: 'Too many requests. Please try again later',
  PLEASE_TRY_AGAIN: 'Please try again',
  CONTACT_SUPPORT: 'Please contact support',
} as const;

// Voice (STT/TTS) errors
export const COHORT_ERROR = {
  ALREADY_IN_OTHER_COHORT:
    'This student is already in another cohort. Use "Move to..." from their current cohort instead.',
  UNASSIGNED_ADD: 'Students cannot be added to Unassigned directly.',
  UNASSIGNED_REMOVE: 'Students cannot be removed from Unassigned.',
  UNASSIGNED_MOVE_TARGET:
    'Students cannot be moved to Unassigned. Remove them from their cohort instead.',
  UNASSIGNED_DELETE: 'The Unassigned cohort cannot be deleted.',
  UNASSIGNED_RENAME: 'The Unassigned cohort cannot be renamed.',
} as const;

export const CATEGORY_ERROR = {
  NO_PERMISSION: 'Only admins can manage categories.',
  NAME_REQUIRED: 'Category name is required.',
  NAME_TOO_LONG: 'Category name must be 50 characters or fewer.',
  NAME_TAKEN: 'A category with this name already exists.',
  NOT_FOUND: 'Category not found.',
} as const;

export const SHARE_ERROR = {
  NOT_OWNER: 'Only the owner of this model can share it.',
  RESHARE_NOT_ALLOWED:
    "The owner of this model doesn't allow sharing it again.",
} as const;

export const FOLDER_ERROR = {
  NAME_REQUIRED: 'Folder name is required.',
  NAME_TOO_LONG: 'Folder name must be 100 characters or fewer.',
  NAME_TAKEN: 'You already have a folder with this name.',
  NOT_FOUND: 'Folder not found.',
  NO_ITEMS: 'Select at least one model.',
  NOT_YOUR_ITEMS: 'You can only add your own models to a folder.',
  NOTHING_TO_SHARE:
    'This folder has no models you can share. Copies you received can be shared only if their owner allows re-sharing.',
  NO_GROUP_PERMISSION: 'You do not have permission to share to this group.',
  NO_COHORT_PERMISSION: 'Only Profs and admins can share to cohort groups.',
  COHORT_NOT_FOUND: 'Cohort group not found.',
  INVITATION_NOT_FOUND: 'Invitation not found.',
} as const;

export const VOICE_ERROR = {
  AUDIO_REQUIRED: 'Audio file is required',
  AUDIO_EMPTY: 'No audio data received. Please try recording again.',
  AUDIO_TOO_SHORT:
    'Recording too short. Please record for at least 1-2 seconds.',
  AUDIO_TOO_LONG: 'Recording too long. Please record a shorter segment.',
  TEXT_REQUIRED: 'Text is required',
  TEXT_TOO_LONG: 'Text is too long',
  TEXT_EMPTY_AFTER_CLEANING: 'Text is empty after removing formatting',
} as const;

// All error message values for pattern matching in error handler
export const USER_FACING_ERROR_MESSAGES = [
  // Authentication
  AUTH_ERROR.INVALID_CREDENTIALS,
  AUTH_ERROR.INVALID_EMAIL_OR_PASSWORD,
  AUTH_ERROR.EMAIL_NOT_VERIFIED,
  AUTH_ERROR.CURRENT_PASSWORD_INCORRECT,
  AUTH_ERROR.INVALID_EMAIL_ADDRESS,
  AUTH_ERROR.INVALID_EMAIL_FORMAT,
  AUTH_ERROR.INVALID_PASSWORD,
  AUTH_ERROR.ACCOUNT_DISABLED,
  AUTH_ERROR.TOO_MANY_LOGIN_ATTEMPTS,
  AUTH_ERROR.SIGN_IN_METHOD_DISABLED,
  AUTH_ERROR.LOGIN_FAILED,
  AUTH_ERROR.EMAIL_ALREADY_EXISTS,
  AUTH_ERROR.REGISTRATION_DISABLED,
  AUTH_ERROR.REGISTRATION_FAILED,
  AUTH_ERROR.PASSWORD_RESET_FAILED,
  AUTH_ERROR.NO_ACCOUNT_FOUND,
  AUTH_ERROR.REFRESH_TOKEN_EXPIRED,
  AUTH_ERROR.INVALID_REFRESH_TOKEN,
  AUTH_ERROR.TURNSTILE_FAILED,
  AUTH_ERROR.INVALID_EMAIL_DOMAIN,
  AUTH_ERROR.EMAIL_ROLE_MISMATCH,
  AUTH_ERROR.COHORT_GROUP_REQUIRED,
  AUTH_ERROR.COHORT_GROUP_NOT_FOUND,

  // Resource
  RESOURCE_ERROR.USER_NOT_FOUND,
  RESOURCE_ERROR.ITEM_NOT_FOUND,
  RESOURCE_ERROR.FILE_NOT_FOUND,
  RESOURCE_ERROR.MODEL_NOT_FOUND,
  RESOURCE_ERROR.ALREADY_EXISTS,
  RESOURCE_ERROR.EXPIRED,

  // Quota
  QUOTA_ERROR.INSUFFICIENT_SCAN_CREDITS,
  QUOTA_ERROR.INSUFFICIENT_EMBED_QUOTA,
  QUOTA_ERROR.SCENE_QUOTA_EXCEEDED,

  // Access
  ACCESS_ERROR.NOT_ITEM_OWNER,
  ACCESS_ERROR.ACCESS_DENIED,

  // Feature
  FEATURE_ERROR.ONLY_PUBLISHED_CAN_EMBED,
  FEATURE_ERROR.ITEM_NOT_PUBLISHED,
  FEATURE_ERROR.SCENE_ADMIN_ONLY,

  // Operation
  OPERATION_ERROR.TOO_MANY_REQUESTS,
  OPERATION_ERROR.PLEASE_TRY_AGAIN,
  OPERATION_ERROR.CONTACT_SUPPORT,

  // Cohort
  COHORT_ERROR.ALREADY_IN_OTHER_COHORT,
  COHORT_ERROR.UNASSIGNED_ADD,
  COHORT_ERROR.UNASSIGNED_REMOVE,
  COHORT_ERROR.UNASSIGNED_MOVE_TARGET,
  COHORT_ERROR.UNASSIGNED_DELETE,
  COHORT_ERROR.UNASSIGNED_RENAME,

  // Category
  CATEGORY_ERROR.NO_PERMISSION,
  CATEGORY_ERROR.NAME_REQUIRED,
  CATEGORY_ERROR.NAME_TOO_LONG,
  CATEGORY_ERROR.NAME_TAKEN,
  CATEGORY_ERROR.NOT_FOUND,

  // Share
  SHARE_ERROR.NOT_OWNER,
  SHARE_ERROR.RESHARE_NOT_ALLOWED,

  // Folder
  FOLDER_ERROR.NAME_REQUIRED,
  FOLDER_ERROR.NAME_TOO_LONG,
  FOLDER_ERROR.NAME_TAKEN,
  FOLDER_ERROR.NOT_FOUND,
  FOLDER_ERROR.NO_ITEMS,
  FOLDER_ERROR.NOT_YOUR_ITEMS,
  FOLDER_ERROR.NOTHING_TO_SHARE,
  FOLDER_ERROR.NO_GROUP_PERMISSION,
  FOLDER_ERROR.NO_COHORT_PERMISSION,
  FOLDER_ERROR.COHORT_NOT_FOUND,
  FOLDER_ERROR.INVITATION_NOT_FOUND,

  // Voice
  VOICE_ERROR.AUDIO_REQUIRED,
  VOICE_ERROR.AUDIO_EMPTY,
  VOICE_ERROR.AUDIO_TOO_SHORT,
  VOICE_ERROR.AUDIO_TOO_LONG,
  VOICE_ERROR.TEXT_REQUIRED,
  VOICE_ERROR.TEXT_TOO_LONG,
  VOICE_ERROR.TEXT_EMPTY_AFTER_CLEANING,
] as const;

export type AuthError = (typeof AUTH_ERROR)[keyof typeof AUTH_ERROR];
export type ResourceError =
  (typeof RESOURCE_ERROR)[keyof typeof RESOURCE_ERROR];
export type QuotaError = (typeof QUOTA_ERROR)[keyof typeof QUOTA_ERROR];
export type AccessError = (typeof ACCESS_ERROR)[keyof typeof ACCESS_ERROR];
export type FeatureError = (typeof FEATURE_ERROR)[keyof typeof FEATURE_ERROR];
export type OperationError =
  (typeof OPERATION_ERROR)[keyof typeof OPERATION_ERROR];
